import { Divider, FlexPanel, Link, Text } from 'Common';
import DynamicFormContainer from 'ExtendedForm/DynamicForm/DynamicFormContainer';
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { setNewPassword } from 'Redux/auth/authThunks';
import { clearFormAction } from 'Redux/forms/formsActions';
import {
  updateProfile,
  updateProfileSettings,
  updateUserLogin
} from 'Redux/profile/profileThunks';
import { LOADING } from 'Redux/userInterface/userInterfaceConstants';
import { t } from 'Utils/localization/i18next';
import { trimAllObjPropertiesSkipPasswords } from 'Utils/object/objectUtils';
import { showToast } from 'Utils/toastUtils';
import styles from './DashboardSectionReadContainer.module.scss';

class DashboardSectionReadContainer extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isEditing: false
    };

    this.myRef = React.createRef();
    this.updateActions = {
      personal: this.props.updateProfile,
      contact: this.props.updateUserLogin,
      credentials: this.props.setNewPassword,
      communication: this.props.updateProfileSettings
    };
  }

  onEdit = () => {
    this.setState({ isEditing: true });
  };

  onClose = () => {
    this.setState({ isEditing: false }, () => {
      this.props.clearFormAction(this.props.editForm);
      this.scrollToCenter();
    });
  };

  showUpdateToast = (hasMemberPanelChanged, isMemberPanelActive) => {
    const OPT_IN = 'communicationMemberPanelOptIn';
    const OPT_OUT = 'communicationMemberPanelOptOut';
    const messages = {
      personal: {
        title: 'dashboard.confirmation.personalSectionUpdateTitle',
        text: 'dashboard.confirmation.personalSectionUpdateText'
      },
      contact: {
        title: 'dashboard.confirmation.contactSectionUpdateTitle',
        text: 'dashboard.confirmation.contactSectionUpdateText'
      },
      credentials: {
        title: 'dashboard.confirmation.passwordSectionUpdateTitle',
        text: 'dashboard.confirmation.passwordSectionUpdateText'
      },
      communication: {
        title: 'dashboard.confirmation.communicationSectionUpdateTitle',
        text: 'dashboard.confirmation.communicationSectionUpdateText'
      },
      [OPT_IN]: {
        title: 'dashboard.confirmation.communicationMemberPanelOptInTitle',
        text: 'dashboard.confirmation.communicationMemberPanelOptInText'
      },
      [OPT_OUT]: {
        title: 'dashboard.confirmation.communicationMemberPanelOptOutTitle',
        text: 'dashboard.confirmation.communicationMemberPanelOptOutText'
      }
    };

    let msgKey = this.props.type;
    if (hasMemberPanelChanged) {
      msgKey = isMemberPanelActive ? OPT_IN : OPT_OUT;
    }
    const msg = messages[msgKey];
    if (msg) {
      showToast(t(msg.title), t(msg.text));
    }
  };

  onSubmit = async data => {
    const updateAction =
      this.updateActions[this.props.type] || this.props.updateProfile;
    const memberPanelOldState = this.props.profile.participateInMemberPanel;
    const mode =
      data.noOfChildren &&
      this.props.profile.noOfChildren &&
      parseInt(data.noOfChildren) !== parseInt(this.props.profile.noOfChildren)
        ? 'SYNC'
        : 'UPSERT';
    const newProfile = { ...this.props.profile, ...data };
    const success = await updateAction(
      trimAllObjPropertiesSkipPasswords(newProfile),
      this.props.editForm,
      null,
      mode
    );

    const isMemberPanelActive = newProfile.participateInMemberPanel;
    const hasMemberPanelChanged = memberPanelOldState !== isMemberPanelActive;

    if (success) {
      this.showUpdateToast(hasMemberPanelChanged, isMemberPanelActive);
      this.setState({ isEditing: false });
    }
    this.scrollToCenter();
  };

  scrollToCenter = () => {
    if (this.myRef) {
      this.myRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  setRef = node => {
    if (node) {
      this.myRef = node;
    }
  };

  render() {
    return (
      <>
        <div ref={this.setRef} className={styles['dashboard-section-read']}>
          <FlexPanel spread className={styles['dashboard-section-read-title']}>
            {this.props.title && (
              <Text bold colour="grey-900">
                {t(this.props.title)}
              </Text>
            )}
            {this.props.editForm && (
              <Link
                bold
                underline
                colour="grey-900"
                id={`${this.props.editForm}-link`}
                onClick={this.state.isEditing ? this.onClose : this.onEdit}
              >
                {this.state.isEditing ? t('common.close') : t('common.edit')}
              </Link>
            )}
          </FlexPanel>
          {this.state.isEditing ? (
            <DynamicFormContainer
              formId={this.props.editForm}
              prefillValues={this.props.profile}
              loadingSubmit={this.props.isLoading}
              textSubmit={t('common.save')}
              textCancel={t('common.cancel')}
              onCancel={this.onClose}
              onSubmit={this.onSubmit}
            />
          ) : (
            this.props.children
          )}
        </div>
        {!this.props.isLastElement && <Divider dashboard />}
      </>
    );
  }
}

DashboardSectionReadContainer.propTypes = {
  title: PropTypes.string,
  editForm: PropTypes.string,
  type: PropTypes.oneOf([
    'personal',
    'contact',
    'credentials',
    'communication'
  ]),
  isLastElement: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node)
  ]),
  // From Redux/Thunks
  profile: PropTypes.object,
  userLogin: PropTypes.string,
  isLoading: PropTypes.bool,
  updateProfile: PropTypes.func.isRequired,
  updateUserLogin: PropTypes.func.isRequired,
  updateProfileSettings: PropTypes.func.isRequired,
  setNewPassword: PropTypes.func.isRequired,
  clearFormAction: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  return {
    profile: state.profile,
    userLogin: state.auth.userLogin,
    isLoading: state.userInterface.loading[LOADING.PROFILE_UPDATE]
  };
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      updateProfile,
      updateUserLogin,
      updateProfileSettings,
      setNewPassword,
      clearFormAction
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DashboardSectionReadContainer);
